import { useState } from "react";
import { Row, Col, Card, Select, Statistic } from "antd";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import useTrainingFeedbacksOverview from "./logic/useTrainingFeedbacksOverview";
import "./TrainingFeedbacksOverview.scss";
const { Option } = Select;

const TrainingFeedbacksOverview = () => {
  const [filter, setFilter] = useState("All");
  const {
    transformedTopFeedbackAspects,
    transformedTraineeTopFeedbackAspects,
    globalTraineeFeedbackStats,
    globalTrainerFeedbackStats
  } = useTrainingFeedbacksOverview();

  const average = (arr) =>
    arr.length === 0
      ? 0
      : (arr.reduce((a, b) => a + b.average, 0) / arr.length).toFixed(2);

  const showTrainer = filter === "All" || filter === "Feedback by trainers";
  const showTrainee = filter === "All" || filter === "Feedbacks by trainees";

  return (
    <div className="training-feedback-overview">
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Select value={filter} onChange={setFilter} style={{ width: "100%" }}>
            <Option value="All">All</Option>
            <Option value="Feedbacks by trainees">Feedbacks by trainees</Option>
            <Option value="Feedback by trainers">Feedback by trainers</Option>
          </Select>
        </Col>
      </Row>

      <Row gutter={[24, 24]} className="dashboard-cards-feedback">
        {showTrainer && (
          <Col xs={24} md={8}>
            <Card className="feedback-card">
              <Statistic
                title="Average Feedback (By Trainers)"
                valueRender={() => (
                  <div className="feedback-value">
                    ⭐ {average(transformedTopFeedbackAspects)} / 5
                  </div>
                )}
              />
            </Card>
          </Col>
        )}

        {showTrainee && (
          <Col xs={24} md={8}>
            <Card className="feedback-card">
              <Statistic
                title="Average Feedback (By Trainees)"
                valueRender={() => (
                  <div className="feedback-value">
                    ⭐ {average(transformedTraineeTopFeedbackAspects)} / 5
                  </div>
                )}
              />
            </Card>
          </Col>
        )}

        {showTrainer && showTrainee && (
          <Col xs={24} md={8}>
            <Card className="feedback-card">
              <Statistic
                title="Average Feedback (Trainers + Trainees)"
                valueRender={() => (
                  <div className="feedback-value">
                    ⭐{" "}
                    {average([
                      ...transformedTopFeedbackAspects,
                      ...transformedTraineeTopFeedbackAspects,
                    ])}{" "}
                    / 5
                  </div>
                )}
              />
            </Card>
          </Col>
        )}
      </Row>

      {showTrainer && (
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col span={16}>
            <Card
              title="Top Feedback Aspects (by Trainers)"
              className="top-feedback-aspects"
            >
              <ResponsiveContainer width="100%" height={300}>
               <BarChart data={transformedTopFeedbackAspects.slice(0, 5)}>
                  <XAxis
                    dataKey="aspect"
                    tickFormatter={(v) => v.replace(/_/g, " ")}
                    angle={-30}
                    textAnchor="end"
                  />
                  <YAxis domain={[0, 5]} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="average" fill="#8884d8" barSize={70} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col span={8}>
            <Card
              title="Average Trainer Feedback Scores"
              className="average-feedback-list scrollable-feedback-list"
            >
              <ul>
                {transformedTopFeedbackAspects.map((item, index) => {
                  const score = item.average.toFixed(2);
                  const aspectLabel = item.aspect.replace(/_/g, " ");
                  let scoreClass = "high";
                  if (item.average < 3.5) scoreClass = "medium";
                  if (item.average < 2.5) scoreClass = "low";

                  return (
                    <li key={index}>
                      <span className="aspect-label">{aspectLabel}</span>
                      <span className={`score-badge ${scoreClass}`}>
                        <span className="stars">
                          {Array.from({ length: 5 }, (_, i) =>
                            item.average >= i + 1
                              ? "★"
                              : item.average >= i + 0.5
                              ? "⯪"
                              : "☆"
                          )}
                        </span>
                        <span className="score-number">({score} / 5)</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </Col>
        </Row>
      )}

      {showTrainee && (
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col span={16}>
            <Card
              title="Top Feedback Aspects (by Trainees)"
              className="top-feedback-aspects"
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={transformedTraineeTopFeedbackAspects}>
                  <XAxis
                    dataKey="aspect"
                    tickFormatter={(v) => v.replace(/_/g, " ")}
                    angle={-30}
                    textAnchor="end"
                  />
                  <YAxis domain={[0, 5]} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="average" fill="#82ca9d" barSize={70} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col span={8}>
            <Card
              title="Average Trainee Feedback Scores"
              className="average-feedback-list scrollable-feedback-list"
            >
              <ul>
                {transformedTraineeTopFeedbackAspects.map((item, index) => {
                  const score = item.average.toFixed(2);
                  const aspectLabel = item.aspect.replace(/_/g, " ");
                  let scoreClass = "high";
                  if (item.average < 3.5) scoreClass = "medium";
                  if (item.average < 2.5) scoreClass = "low";

                  return (
                    <li key={index}>
                      <span className="aspect-label">{aspectLabel}</span>
                      <span className={`score-badge ${scoreClass}`}>
                        <span className="stars">
                          {Array.from({ length: 5 }, (_, i) =>
                            item.average >= i + 1
                              ? "★"
                              : item.average >= i + 0.5
                              ? "⯪"
                              : "☆"
                          )}
                        </span>
                        <span className="score-number">({score} / 5)</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </Col>
        </Row>
      )}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        {showTrainer && (
<Col span={12}>
  <Card title="Training Feedback Analysis (By Trainers)">
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={[
            {
              name: "Positive",
              value: parseFloat(
                globalTrainerFeedbackStats.positive || "0"
              ),
            },
            {
              name: "Neutral",
              value: parseFloat(
                globalTrainerFeedbackStats.neutral|| "0"
              ),
            },
            {
              name: "Negative",
              value: parseFloat(
                globalTrainerFeedbackStats.negative || "0"
              ),
            },
          ]}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={80}
          label={({ name, percent }) =>
            `${name} ${(percent * 100).toFixed(0)}%`
          }
        >
          <Cell fill="#82ca9d" /> {/* Positive */}
          <Cell fill="#f79635" /> {/* Neutral */}
          <Cell fill="#ff4538" /> {/* Negative */}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  </Card>
</Col>

        )}

        {showTrainee && (
          <Col span={12}>
            <Card title="Training Feedback Analysis (By Trainees)">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      {
                        name: "Positive",
                        value: parseFloat(
                          globalTraineeFeedbackStats.levels?.positive
                            ?.percentage || "0"
                        ),
                      },
                      {
                        name: "Neutral",
                        value: parseFloat(
                          globalTraineeFeedbackStats.levels?.neutral
                            ?.percentage || "0"
                        ),
                      },
                      {
                        name: "Negative",
                        value: parseFloat(
                          globalTraineeFeedbackStats.levels?.negative
                            ?.percentage || "0"
                        ),
                      },
                    ]}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    <Cell fill="#82ca9d" /> {/* Positive */}
                    <Cell fill="#f79635" /> {/* Neutral */}
                    <Cell fill="#ff4538" /> {/* Negative */}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default TrainingFeedbacksOverview;
